import React, { useState, useEffect, useCallback } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';
import recruitingService from '../utils/recruitingService';
import './HiresBySourceChart.css';

/**
 * HiresBySourceChart Component
 * Pie/donut chart showing hires by candidate source
 */
function HiresBySourceChart({ filters }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await recruitingService.getHiresBySource(filters);
            if (response.success && response.data) {
                setData(response.data);
            }
        } catch (err) {
            console.error('Error fetching hires by source:', err);
            setData([]);
        }
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Colors for pie slices
    const COLORS = ['#E67E22', '#3498DB', '#27AE60', '#9B59B6', '#E74C3C', '#1ABC9C', '#F1C40F', '#34495E', '#95A5A6', '#2980B9'];

    // Custom tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="chart-tooltip">
                    <p className="tooltip-label">{data.source}</p>
                    <p className="tooltip-value">
                        <span className="tooltip-count">{data.hires}</span> hires
                    </p>
                    <p className="tooltip-rate">
                        {data.conversionRate}% conversion rate
                    </p>
                </div>
            );
        }
        return null;
    };

    // Custom legend
    const renderLegend = (props) => {
        const { payload } = props;
        return (
            <ul className="custom-legend">
                {payload.map((entry, index) => (
                    <li key={`item-${index}`} className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: entry.color }}></span>
                        <span className="legend-text">{entry.value}</span>
                        <span className="legend-value">{data[index]?.hires || 0}</span>
                    </li>
                ))}
            </ul>
        );
    };

    if (loading) {
        return (
            <div className="hires-by-source-chart chart-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="hires-by-source-chart chart-empty">
                <p>No source data available</p>
            </div>
        );
    }

    // Calculate total for center display
    const totalHires = data.reduce((sum, d) => sum + d.hires, 0);

    return (
        <div className="hires-by-source-chart">
            <div className="chart-header">
                <div className="chart-title-section">
                    <h3 className="chart-title">Hires by Source</h3>
                    <span className="chart-subtitle">Where candidates come from</span>
                </div>
            </div>
            <div className="chart-body">
                <div className="pie-container">
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={90}
                                paddingAngle={2}
                                dataKey="hires"
                                nameKey="source"
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="donut-center">
                        <span className="donut-total">{totalHires}</span>
                        <span className="donut-label">Hires</span>
                    </div>
                </div>
                <div className="source-legend">
                    {data.slice(0, 6).map((item, index) => (
                        <div key={index} className="legend-row">
                            <span className="legend-dot" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                            <span className="legend-source">{item.source}</span>
                            <span className="legend-hires">{item.hires}</span>
                            <span className="legend-rate">{item.conversionRate}%</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default HiresBySourceChart;
